import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {
  getHotelOfferEntityOffers,
  getShowTotalPrices,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {
  getFilteredRoomsData,
  getIsFilteredRoomsResultEmpty,
  getIsRoomsPollingWaitingForResults,
  getIsRoomsQueryLoading
} from 'modules/searchApi/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {applyRoundingStrategy} from '@vio/core/src/utils/number'
import {useShouldShowDealFreezeButton} from '@vio/shared/src/dealFreeze/hooks/useShouldShowDealFreezeButton'
import {isOfferEligibleForDealFreeze} from '@vio/shared/src/dealFreeze/utils/OfferEligibility'

import {DetailedPrice, PriceTrendsParams} from '../services/priceInsightsApi'
import {
  isRoomOffer,
  isSearchOffer,
  PriceTrendContent,
  PriceTrendInsightsPayload,
  PriceTrendOffer
} from '../types'
import {buildPriceTrendContent} from '../utils/contentBuilder'
import {PriceTrendMessageKey} from '../utils/messages'
import {usePriceTrendInsights} from './usePriceTrendInsights'

interface UsePriceTrendParams {
  /** Pre-provided insights (e.g., for Storybook) */
  insights?: PriceTrendInsightsPayload
  /** Normalized fetch parameters */
  fetchParams?: Partial<PriceTrendsParams>
  /**
   * Latest similar-hotels percentile distribution in the API's native unit
   * (full-tax stay total). When provided, the recommendation classifier
   * uses it (percentile bucket → banner copy) so the banner stays in sync
   * with the `PriceRangeBar` indicator. Without it, the classifier falls
   * back to the median-distance signal.
   *
   * IMPORTANT: pass `latestDetailedPriceForClassifier` from `usePriceHistory`
   * — not the display-unit (`…ForDisplay`) variant that labels the bar.
   * The classifier comparator is `primaryOfferData.totalPrice` (also a
   * full-tax stay total); mixing units would produce a wrong direction.
   */
  detailedPrice?: DetailedPrice | null
}

interface UsePriceTrendReturn {
  // Insights data
  insights: PriceTrendInsightsPayload | undefined
  isLoading: boolean
  isError: boolean

  // Content (recommendation + cards)
  content: PriceTrendContent | null

  // Button content (cheapest offer from room section)
  buttonContent: {
    messageKey: PriceTrendMessageKey | null
    priceValue: number | null
    currency: string | null
    nightlyPrice: number | null
  }

  // Selected offer (passed through for OfferInteraction components)
  selectedOffer: PriceTrendOffer | undefined

  // Whether the selected offer may surface deal-freeze UI: the full rooms-list
  // gate (brand toggle, auth, user eligibility, frozen bookings, offer errors)
  // AND the deal_freeze tag on the displayed offer. Drives the freeze button
  // label and the Our Advice freeze verdict.
  isFreezable: boolean

  // Full-tax classifier comparator for the similar-hotels band; same unit as
  // the raw API median / detailedPrice (not the display-unit priceValue).
  classifierPrice: number

  // Derived flags
  isPrimaryOfferSplitBooking: boolean
  hotelId: string | undefined
}

/**
 * Master hook for Price Trend component.
 * Consolidates insights fetching, offer analysis, and content calculation.
 *
 * Offer selection based on isVioOfferOrBundleSelected():
 * - true: Room offer from getFilteredRoomsData (Rooms API) - Vio/BOFH offers
 * - false: Search offer from getHotelOfferEntityOffers (Search API) - redirect offers
 *
 * This ensures PriceTrend works in both scenarios:
 * - Vio offer selected: Shows room offer price (BOFH)
 * - Redirect offer: Shows top offer price from Search API
 *
 * @param params - Configuration for insights and fetching
 * @returns Complete price trend data and state
 */
export const usePriceTrend = ({
  insights: insightsProp,
  fetchParams,
  detailedPrice
}: UsePriceTrendParams): UsePriceTrendReturn => {
  const hotelId = fetchParams?.hotelId
  const normalizedHotelId = hotelId ? String(hotelId) : undefined

  // Determine device type to match the correct component logic
  // Desktop: AccommodationOfferBox - uses Rooms API when !hasNoRooms
  // Mobile: BottomCTA - always uses Search API (first offer from topOffers)
  const {isMobile} = useDeviceLayout()

  // Determine offer source based on device type
  const hasNoRooms = useSelector(getIsFilteredRoomsResultEmpty)
  const isVioOfferSelectedFromUrl = isVioOfferOrBundleSelected()

  // Desktop: Match AccommodationOfferBox - use Rooms API when rooms are available
  // Mobile: Match BottomCTA - always use Search API (ignore rooms availability)
  const isVioOfferSelected = isMobile
    ? isVioOfferSelectedFromUrl // Mobile: only use Rooms API if URL explicitly selects it
    : isVioOfferSelectedFromUrl || !hasNoRooms // Desktop: use Rooms API if URL selects it OR rooms are available

  // Source 1: Room offer from Rooms API (Vio/BOFH offers)
  const {rooms} = useSelector(getFilteredRoomsData)
  const isRoomsLoading = useSelector(getIsRoomsQueryLoading)
  const isRoomsPollingWaitingForResults = useSelector(
    getIsRoomsPollingWaitingForResults
  )

  // Hide PriceTrend only when SB is the FIRST offer shown in the TopOffers
  // anchor (same signal `OfferBox` uses to render the SB primary card). Using
  // the anchor signal — rather than the Rooms API entity sort — keeps the
  // gating consistent with what the user actually sees at the top of the AP
  // and avoids a flash-then-hide when Rooms ranks SB first but the anchor
  // surfaces a regular offer (e.g., SB promoted to position 2).
  const splitBookingDetails = useSelector(state =>
    normalizedHotelId ? getSplitBookingDetails(state, normalizedHotelId) : null
  )
  const isPrimaryOfferSplitBooking = Boolean(
    splitBookingDetails?.hasPrimarySplitBookingOffer
  )

  const room = rooms[0]
  const offer = room?.offers[0]

  // Only select room offer if rooms are loaded to prevent race condition
  // Match PrimaryRoomOffer behavior: wait for polling to complete
  // Note: pickedEntity is always Regular (split booking filtered out above)
  const roomOffer = useMemo(() => {
    if (isRoomsLoading || isRoomsPollingWaitingForResults || !offer) {
      return undefined
    }
    return offer
  }, [isRoomsLoading, isRoomsPollingWaitingForResults, offer])

  // Source 2: Search offer from Search API (redirect offers)
  const topOffers = useSelector(state =>
    normalizedHotelId
      ? getHotelOfferEntityOffers(state, normalizedHotelId)
      : undefined
  )
  // Match AccommodationOfferBox: use first offer (topOffers[0]) instead of cheapest by nightly rate
  // This ensures PriceTrend selects the same offer as AccommodationOfferBox
  const searchOffer = topOffers?.[0]

  // Select offer: match AccommodationOfferBox logic
  // - If rooms are available (!hasNoRooms), use Rooms API offer (like PrimaryRoomOffer)
  // - Otherwise, use Search API offer (like SecondaryOfferList)
  // If primary offer is split booking, return undefined to hide PriceTrend
  // When rooms are still loading, fall back to search offer so Price Trends
  // can render as soon as the search API completes (without waiting for rooms).
  // Once rooms finish loading, roomOffer will be defined and take priority.
  const selectedOffer: PriceTrendOffer | undefined = isPrimaryOfferSplitBooking
    ? undefined
    : isVioOfferSelected
      ? (roomOffer ?? searchOffer)
      : searchOffer

  // Fetch insights (if not provided)
  const {
    insights: fetchedInsights,
    isLoading,
    isError
  } = usePriceTrendInsights(insightsProp ? undefined : fetchParams)

  const insights = insightsProp || fetchedInsights

  // Calculate price data from selected offer.
  //
  // PriceTrend mirrors the adjacent OfferBox card's per-mode contract so the
  // bubble + bar + chart never disagree with the offer card the user is
  // looking at:
  //   - **Total mode** (`showTotalPrices=true`): full-tax stay total.
  //     OfferBox renders "PLN X total price with taxes & fees" using
  //     `selectedOffer.totalRate` (SearchOffer) / `getTotalPrice(displayPrice)`
  //     (Room Offer) — both always full-tax. We read the same fields here.
  //   - **Nightly mode** (`showTotalPrices=false`): per-night, **honouring**
  //     the user's `includeTaxes` / `includeLocalTaxes` prefs. OfferBox uses
  //     `selectedOffer.nightlyRate` / `displayPrice.chargeable.nightlyDisplayTotal`
  //     — both are already pref-respecting from the offer mapping. We read
  //     the same fields here.
  //
  // Classifier comparator (downstream in `content` useMemo) uses
  // `primaryOfferData.totalPrice` (full-tax) against the raw API median
  // (also full-tax) — unit-invariant regardless of `showTotalPrices`.
  const primaryOfferData = useMemo(() => {
    if (!selectedOffer) {
      return {
        offerId: undefined,
        totalPrice: 0,
        nightlyPrice: 0,
        currency: undefined
      }
    }

    // SearchOffer has different price structure (rate object instead of prices array)
    if (isSearchOffer(selectedOffer)) {
      return {
        offerId: selectedOffer.id,
        totalPrice: selectedOffer.totalRate ?? 0,
        nightlyPrice:
          selectedOffer.nightlyRate ?? selectedOffer.rate?.base ?? 0,
        currency: selectedOffer.currency
      }
    }

    // Room Offer has prices array
    const displayPrice = getDisplayPrice(selectedOffer.prices)
    const totalPrice = getTotalPrice(displayPrice)
    const nightlyPrice = displayPrice?.chargeable?.nightlyDisplayTotal ?? 0
    const currency = displayPrice?.chargeable?.currencyCode ?? undefined

    return {
      offerId: selectedOffer.id,
      totalPrice,
      nightlyPrice,
      currency
    }
  }, [selectedOffer])

  const showTotalPrices = useSelector(getShowTotalPrices)

  // Full deal-freeze gate (brand toggle, auth, user eligibility, frozen
  // bookings, offer errors), same as the rooms list, so the freeze label is
  // only shown when the user can actually freeze. The hook tolerates an
  // undefined offer while offers are loading. Sold-out and app-locked are
  // left to their false defaults: sold-out surfaces don't render PriceTrend,
  // and the rooms list's own gate still applies after the scroll.
  const shouldShowDealFreezeButton = useShouldShowDealFreezeButton({
    offer: selectedOffer,
    checkIn: fetchParams?.checkIn,
    checkOut: fetchParams?.checkOut,
    hotelId: normalizedHotelId
  })
  // The gate alone returns true for ANY BOFH offer when the user holds a
  // frozen booking at this hotel (a rooms-list affordance). PriceTrend's
  // freeze label/CTA describes the displayed offer, so it additionally
  // requires the deal_freeze tag on that offer.
  const isFreezable =
    shouldShowDealFreezeButton &&
    isOfferEligibleForDealFreeze(selectedOffer?.tags)

  const buttonContent = useMemo(() => {
    if (!primaryOfferData.offerId) {
      return {
        messageKey: null,
        priceValue: null,
        currency: null,
        nightlyPrice: null
      }
    }

    // Check if selected offer is a BOFH offer
    // Room Offers are always BOFH, SearchOffers need to check intermediaryProvider
    const isSelectedOfferBofh =
      isRoomOffer(selectedOffer) ||
      (isSearchOffer(selectedOffer) && isBofhOffer(selectedOffer))

    // Determine message key: BOFH offers show "View Rooms", others show "View Deal" or deal freeze
    let messageKey: PriceTrendMessageKey
    if (isFreezable) {
      messageKey = 'freezeDealsFromValue'
    } else if (isSelectedOfferBofh) {
      messageKey = 'viewRoomsFromValue'
    } else {
      messageKey = 'viewDealFromValue'
    }

    const priceValue = applyRoundingStrategy(
      'round',
      showTotalPrices
        ? primaryOfferData.totalPrice
        : primaryOfferData.nightlyPrice
    )

    const result = {
      messageKey,
      priceValue,
      currency: primaryOfferData.currency ?? null,
      nightlyPrice: primaryOfferData.nightlyPrice ?? null
    }

    return result
  }, [primaryOfferData, isFreezable, showTotalPrices, selectedOffer])

  // Classifier input. Compare the offer's full-tax stay total
  // (`primaryOfferData.totalPrice`) against the raw API median — both are
  // already in the "total stay WITH taxes" unit the BE returns. No tax
  // adjustment needed: the percentage is unit-invariant when both sides
  // share a unit, so the banner copy is correct regardless of the user's
  // `includeTaxes` / `showTotalPrices` preferences.
  //
  // `detailedPrice` must be passed in the same unit (raw, full-tax) — see
  // the doc comment on `UsePriceTrendParams.detailedPrice`.
  const content = useMemo(() => {
    if (!insights || !primaryOfferData.offerId) {
      return null
    }

    return buildPriceTrendContent(
      insights,
      primaryOfferData.totalPrice,
      detailedPrice
    )
  }, [
    insights,
    primaryOfferData.offerId,
    primaryOfferData.totalPrice,
    detailedPrice
  ])

  return {
    insights,
    isLoading,
    isError,
    content,
    buttonContent,
    selectedOffer,
    isFreezable,
    classifierPrice: primaryOfferData.totalPrice,
    isPrimaryOfferSplitBooking,
    hotelId: normalizedHotelId
  }
}
